import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../../components/data-table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  rolesPermissionsAPI,
  superAdminUsersAPI,
  type Permission,
  type Group,
  type UserListItem,
} from "../../services/api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Shield, Plus, Pencil, Trash2, Component } from "lucide-react";
import { Checkbox } from "../../components/ui/checkbox";

export default function RolesPermissions() {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);

  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Group Dialog
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [groupName, setGroupName] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>(
    []
  );
  const [actionLoading, setActionLoading] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);

  // Assign Dialog
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);

  const [isAssignPermissionsDialogOpen, setIsAssignPermissionsDialogOpen] =
    useState(false);
  const [selectedUserPermissionIds, setSelectedUserPermissionIds] = useState<
    number[]
  >([]);

  const [activeTab, setActiveTab] = useState("groups");

  useEffect(() => {
    if (permissions.length === 0) {
      fetchPermissions();
    }

    if (activeTab === "groups") {
      fetchGroups();
    } else if (activeTab === "assignment") {
      fetchUsers();
      if (groups.length === 0) {
        fetchGroups();
      }
    }
  }, [activeTab, groups.length, permissions.length]);

  const fetchPermissions = async () => {
    setLoadingPermissions(true);
    try {
      const res = await rolesPermissionsAPI.getPermissions("jbf");
      setPermissions(Array.isArray(res) ? res : []);
      // Some APIs might return { data: [...] } instead of direct array, assuming direct array based on interceptor
    } catch {
      toast.error("Failed to fetch permissions");
    } finally {
      setLoadingPermissions(false);
    }
  };

  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const res = await rolesPermissionsAPI.getGroups({
        page: 1,
        page_size: 1000,
      });
      if (res?.results) {
        setGroups(res.results);
      } else if (Array.isArray(res)) {
        setGroups(res);
      } else if (res?.data) {
        setGroups(res.data.results ?? res.data);
      }
    } catch {
      toast.error("Failed to fetch groups");
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await superAdminUsersAPI.list({ page: 1, page_size: 1000 });
      if (res?.results) {
        setUsers(res.results);
      } else if (Array.isArray(res.data)) {
        setUsers(res.data);
      }
    } catch {
      toast.error("Failed to fetch users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const openGroupDialog = (group?: Group) => {
    if (group) {
      setEditingGroup(group);
      setGroupName(group.name);
      setSelectedPermissionIds(group.permissions || []);
    } else {
      setEditingGroup(null);
      setGroupName("");
      setSelectedPermissionIds([]);
    }
    setIsGroupDialogOpen(true);
  };

  const saveGroup = async () => {
    if (!groupName.trim()) {
      toast.error("Group name is required");
      return;
    }
    setActionLoading(true);
    try {
      if (editingGroup) {
        await rolesPermissionsAPI.updateGroup(editingGroup.id, {
          name: groupName,
          permissions: selectedPermissionIds,
        });
        toast.success("Group updated successfully");
      } else {
        await rolesPermissionsAPI.createGroup({
          name: groupName,
          permissions: selectedPermissionIds,
        });
        toast.success("Group created successfully");
      }
      setIsGroupDialogOpen(false);
      fetchGroups();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Failed to save group");
    } finally {
      setActionLoading(false);
    }
  };

  const executeDeleteGroup = async () => {
    if (!groupToDelete) {
      return;
    }
    setActionLoading(true);
    try {
      await rolesPermissionsAPI.deleteGroup(groupToDelete.id);
      toast.success("Group deleted successfully");
      setGroupToDelete(null);
      fetchGroups();
    } catch {
      toast.error("Failed to delete group");
    } finally {
      setActionLoading(false);
    }
  };

  const openAssignDialog = (user: UserListItem) => {
    setSelectedUser(user);
    if (user.groups) {
      setSelectedGroupIds(user.groups.map(g => g.id));
    } else {
      setSelectedGroupIds([]);
    }
    setIsAssignDialogOpen(true);
  };

  const assignGroupsToUser = async () => {
    if (!selectedUser) {
      return;
    }
    setActionLoading(true);
    try {
      await rolesPermissionsAPI.assignGroupsToUser(
        selectedUser.id,
        selectedGroupIds
      );
      toast.success("Groups assigned successfully");
      setIsAssignDialogOpen(false);
      fetchUsers(); // Refresh to get updated groups/permissions
    } catch {
      toast.error("Failed to assign groups");
    } finally {
      setActionLoading(false);
    }
  };

  const openAssignPermissionsDialog = useCallback(
    (user: UserListItem) => {
      setSelectedUser(user);
      if (user.permissions && Array.isArray(user.permissions)) {
        // API returns permissions like "JBF.add_gallery"
        // We map these back to precision IDs via codename (e.g., "add_gallery")
        const userPermCodenames = user.permissions.map(p => p.split(".").pop());
        const permIds = permissions
          .filter(p => userPermCodenames.includes(p.codename))
          .map(p => p.id);
        setSelectedUserPermissionIds(permIds);
      } else {
        setSelectedUserPermissionIds([]);
      }
      setIsAssignPermissionsDialogOpen(true);
    },
    [permissions]
  );

  const assignPermissionsToUser = async () => {
    if (!selectedUser) {
      return;
    }
    setActionLoading(true);
    try {
      await rolesPermissionsAPI.assignPermissionsToUser(
        selectedUser.id,
        selectedUserPermissionIds
      );
      toast.success("Permissions assigned successfully");
      setIsAssignPermissionsDialogOpen(false);
      fetchUsers(); // Refresh to get updated groups/permissions
    } catch {
      toast.error("Failed to assign permissions");
    } finally {
      setActionLoading(false);
    }
  };

  // Grouping permissions by content_type for better UI
  const permissionsByGroup = permissions.reduce(
    (acc, perm) => {
      const parts = perm.content_type.split(" | ");
      const category = parts[parts.length - 1] || "Others";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(perm);
      return acc;
    },
    {} as Record<string, Permission[]>
  );

  const groupsTableColumns = useMemo<TableColumnDef<Group>[]>(
    () => [
      {
        id: "id",
        label: "ID",
        width: 72,
        renderCell: g => g.id,
      },
      {
        id: "name",
        label: "Name",
        width: 180,
        renderCell: g => <span className="font-medium">{g.name}</span>,
      },
      {
        id: "permissionsCount",
        label: "Permissions Count",
        width: 160,
        renderCell: g => (
          <Badge variant="secondary">
            {g.permissions?.length || 0} Permissions
          </Badge>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        width: 100,
        alwaysVisible: true,
        headerClassName: "text-right",
        cellClassName: "text-right",
        renderCell: g => (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openGroupDialog(g)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive"
              onClick={() => setGroupToDelete(g)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        ),
      },
    ],
    []
  );

  const usersTableColumns = useMemo<TableColumnDef<UserListItem>[]>(
    () => [
      {
        id: "name",
        label: "Name",
        width: 160,
        renderCell: u => u.full_name ?? "-",
      },
      {
        id: "emailPhone",
        label: "Email & Phone",
        width: 200,
        renderCell: u => (
          <div className="flex flex-col text-sm">
            <span>{u.email}</span>
            <span className="text-xs text-muted-foreground">
              {u.phone_number ?? "-"}
            </span>
          </div>
        ),
      },
      {
        id: "assignGroup",
        label: "Assign Group",
        width: 180,
        renderCell: u => (
          <div className="flex flex-col flex-wrap gap-1">
            {u.groups && u.groups.length > 0 ? (
              u.groups.map(g => (
                <span
                  key={g.id}
                  className="flex items-center gap-2 text-[13px] font-medium text-[#d4a017]"
                >
                  <Component className="h-3 w-3" /> {g.name}
                </span>
              ))
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            )}
          </div>
        ),
      },
      {
        id: "currentRole",
        label: "Current Role",
        width: 120,
        renderCell: u => (
          <Badge className="capitalize">
            {u.jbf_role?.replace("_", " ") ?? "None"}
          </Badge>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        width: 280,
        alwaysVisible: true,
        headerClassName: "text-right",
        cellClassName: "space-x-2 text-right",
        renderCell: u => (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openAssignDialog(u)}
            >
              Assign Groups
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openAssignPermissionsDialog(u)}
            >
              Assign Direct Permissions
            </Button>
          </>
        ),
      },
    ],
    [openAssignPermissionsDialog]
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Role & Permission Management
        </h1>
        <p className="text-muted-foreground">
          Manage groups, permissions, and assign them to users
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="groups">Groups</TabsTrigger>
          <TabsTrigger value="assignment">User Allocation</TabsTrigger>
          <TabsTrigger value="permissions">Permissions List</TabsTrigger>
        </TabsList>

        <TabsContent value="groups">
          <DataTableProvider
            tableId="super-admin-roles-groups"
            columns={groupsTableColumns}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <CardTitle>All Groups</CardTitle>
                <div className="flex items-center gap-2">
                  <Button onClick={() => openGroupDialog()} className="gap-2">
                    <Plus className="h-4 w-4" /> Create Group
                  </Button>
                  <DataTableColumnSettings columns={groupsTableColumns} />
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                {loadingGroups ? (
                  <p className="py-8 text-center text-muted-foreground">
                    Loading groups...
                  </p>
                ) : (
                  <DataTable
                    embedded
                    hideToolbar
                    tableId="super-admin-roles-groups"
                    columns={groupsTableColumns}
                    data={groups}
                    getRowKey={g => g.id}
                    emptyMessage="No groups found."
                  />
                )}
              </CardContent>
            </Card>
          </DataTableProvider>
        </TabsContent>

        <TabsContent value="assignment">
          <Card>
            <CardContent className="p-3 pb-4">
              {loadingUsers ? (
                <p className="py-8 text-center text-muted-foreground">
                  Loading users...
                </p>
              ) : (
                <DataTable
                  title={`Assign Groups to Users (${users.length})`}
                  tableId="super-admin-roles-user-assignment"
                  columns={usersTableColumns}
                  data={users}
                  getRowKey={u => u.id}
                  emptyMessage="No users found."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="permissions">
          <Card>
            <CardHeader>
              <CardTitle>System Permissions</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingPermissions ? (
                <p>Loading...</p>
              ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Object.keys(permissionsByGroup)
                    .sort()
                    .map(cat => (
                      <div key={cat} className="space-y-3">
                        <h3 className="border-b pb-1 text-lg font-semibold">
                          {cat}
                        </h3>
                        <ul className="space-y-2 text-sm">
                          {permissionsByGroup[cat].map(p => (
                            <li key={p.id} className="flex items-start gap-2">
                              <Shield className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                              <span>
                                {p.name}{" "}
                                <span className="text-xs text-muted-foreground">
                                  ({p.codename})
                                </span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Group Dialog */}
      <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? "Edit Group" : "Create Group"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto pb-4 pr-2">
            <div>
              <Label>Group Name</Label>
              <Input
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                placeholder="e.g. jbf_management"
              />
            </div>

            <div>
              <div className="mb-2 mt-4 flex items-center justify-between border-b pb-2">
                <Label className="text-base font-semibold">
                  Select Permissions
                </Label>
                <div className="space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setSelectedPermissionIds(permissions.map(p => p.id))
                    }
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedPermissionIds([])}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {Object.keys(permissionsByGroup)
                  .sort()
                  .map(cat => (
                    <Card key={cat} className="overflow-hidden">
                      <div className="flex items-center justify-between border-b bg-muted px-4 py-2 text-sm font-medium">
                        {cat}
                        <Checkbox
                          checked={permissionsByGroup[cat].every(p =>
                            selectedPermissionIds.includes(p.id)
                          )}
                          onCheckedChange={checked => {
                            const catIds = permissionsByGroup[cat].map(
                              p => p.id
                            );
                            if (checked) {
                              setSelectedPermissionIds(prev =>
                                Array.from(new Set([...prev, ...catIds]))
                              );
                            } else {
                              setSelectedPermissionIds(prev =>
                                prev.filter(id => !catIds.includes(id))
                              );
                            }
                          }}
                        />
                      </div>
                      <div className="max-h-48 space-y-3 overflow-y-auto p-4">
                        {permissionsByGroup[cat].map(p => (
                          <div
                            key={p.id}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`perm-${p.id}`}
                              checked={selectedPermissionIds.includes(p.id)}
                              onCheckedChange={checked => {
                                if (checked) {
                                  setSelectedPermissionIds(prev => [
                                    ...prev,
                                    p.id,
                                  ]);
                                } else {
                                  setSelectedPermissionIds(prev =>
                                    prev.filter(id => id !== p.id)
                                  );
                                }
                              }}
                            />
                            <label
                              htmlFor={`perm-${p.id}`}
                              className="cursor-pointer select-none text-sm leading-tight"
                            >
                              {p.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              variant="outline"
              onClick={() => setIsGroupDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={saveGroup} disabled={actionLoading}>
              {actionLoading ? "Saving..." : "Save Group"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign User to Group Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Groups</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6 py-4">
              <div className="space-y-1">
                <p className="font-medium">
                  {selectedUser.full_name ?? selectedUser.email}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedUser.email}
                </p>
              </div>

              <div className="space-y-4">
                <Label>Select Groups</Label>
                <div className="max-h-[40vh] space-y-3 overflow-y-auto rounded-md border p-4">
                  {groups.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No groups available.
                    </p>
                  ) : null}
                  {groups.map(g => (
                    <div
                      key={g.id}
                      className="flex items-center space-x-3 rounded bg-muted/30 p-2 transition-colors hover:bg-muted/50"
                    >
                      <Checkbox
                        id={`group-${g.id}`}
                        checked={selectedGroupIds.includes(g.id)}
                        onCheckedChange={checked => {
                          if (checked) {
                            setSelectedGroupIds(prev => [...prev, g.id]);
                          } else {
                            setSelectedGroupIds(prev =>
                              prev.filter(id => id !== g.id)
                            );
                          }
                        }}
                      />
                      <label
                        htmlFor={`group-${g.id}`}
                        className="flex-1 cursor-pointer text-sm font-medium"
                      >
                        {g.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsAssignDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={assignGroupsToUser} disabled={actionLoading}>
                  {actionLoading ? "Assigning..." : "Assign Groups"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Direct Permissions Dialog */}
      <Dialog
        open={isAssignPermissionsDialogOpen}
        onOpenChange={setIsAssignPermissionsDialogOpen}
      >
        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col">
          <DialogHeader>
            <DialogTitle>Assign Direct Permissions</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="flex flex-1 flex-col space-y-4 overflow-hidden">
              <div className="space-y-1">
                <p className="font-medium">
                  {selectedUser.full_name ?? selectedUser.email}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedUser.email}
                </p>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto pb-4 pr-2">
                <div className="mb-2 mt-4 flex items-center justify-between border-b pb-2">
                  <Label className="text-base font-semibold">
                    Select Direct Permissions
                  </Label>
                  <div className="space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setSelectedUserPermissionIds(permissions.map(p => p.id))
                      }
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedUserPermissionIds([])}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {Object.keys(permissionsByGroup)
                    .sort()
                    .map(cat => (
                      <Card key={cat} className="overflow-hidden">
                        <div className="flex items-center justify-between border-b bg-muted px-4 py-2 text-sm font-medium">
                          {cat}
                          <Checkbox
                            checked={permissionsByGroup[cat].every(p =>
                              selectedUserPermissionIds.includes(p.id)
                            )}
                            onCheckedChange={checked => {
                              const catIds = permissionsByGroup[cat].map(
                                p => p.id
                              );
                              if (checked) {
                                setSelectedUserPermissionIds(prev =>
                                  Array.from(new Set([...prev, ...catIds]))
                                );
                              } else {
                                setSelectedUserPermissionIds(prev =>
                                  prev.filter(id => !catIds.includes(id))
                                );
                              }
                            }}
                          />
                        </div>
                        <div className="max-h-48 space-y-3 overflow-y-auto p-4">
                          {permissionsByGroup[cat].map(p => (
                            <div
                              key={p.id}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                id={`user-perm-${p.id}`}
                                checked={selectedUserPermissionIds.includes(
                                  p.id
                                )}
                                onCheckedChange={checked => {
                                  if (checked) {
                                    setSelectedUserPermissionIds(prev => [
                                      ...prev,
                                      p.id,
                                    ]);
                                  } else {
                                    setSelectedUserPermissionIds(prev =>
                                      prev.filter(id => id !== p.id)
                                    );
                                  }
                                }}
                              />
                              <label
                                htmlFor={`user-perm-${p.id}`}
                                className="cursor-pointer select-none text-sm leading-tight"
                              >
                                {p.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </Card>
                    ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsAssignPermissionsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={assignPermissionsToUser}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Assigning..." : "Assign Permissions"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Delete Group Confirmation Dialog */}
      <Dialog
        open={!!groupToDelete}
        onOpenChange={open => !open && setGroupToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Group</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete the group{" "}
              <strong className="text-foreground">{groupToDelete?.name}</strong>
              ? This action cannot be undone, and users with this group will
              lose these permissions.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setGroupToDelete(null)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="text-white"
              onClick={executeDeleteGroup}
              disabled={actionLoading}
            >
              {actionLoading ? "Deleting..." : "Delete Group"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
